import { useCallback, useEffect, useMemo, useState } from "react";

import { t } from "../i18n";
import { Icons } from "../lib/icons";
import { Logo, Mark } from "../lib/logo";
import { PageHead } from "./Shell";
import {
  AdminError, admin, type BrandCheck, type BrandNote, type BrandPayload, type BrandRow,
  type BrandTexts, type BrandTokens,
} from "./api";
import type { Locale } from "../types";

/**
 * Szablony marki: skórka procesu kandydata dla każdej marki na platformie.
 *
 * Szablon to tylko skórka — kolory, logo, powitanie i finał. Układ, pola
 * i kolejność pytań zostają te same, więc tu nie ma niczego, co dotyka
 * procesu. Panel konsultanta zostaje w barwach for apps; markę widać
 * wyłącznie w podglądzie telefonu.
 *
 * Kolory odcieni, kontrast i uwagi liczy serwer (`/brands/derive`) — tą samą
 * funkcją, która koloruje proces kandydata. Podgląd nie może pokazać czegoś
 * innego, niż kandydat dostanie w linku.
 */

const CLASSIC = "classic";
const JEZYKI: Locale[] = ["pl", "en", "ru", "uk"];
const HEX = /^#[0-9a-fA-F]{6}$/;
const LOGO_MAX = 300 * 1024;
const LOGO_TYPY = ["image/png", "image/svg+xml", "image/webp", "image/jpeg"];

const KOLORY: { key: string; label: string; hint: string }[] = [
  { key: "primary", label: "Akcja", hint: "przyciski — jedyne pełne wypełnienie" },
  { key: "primary-strong", label: "Akcja — hover", hint: "najechanie, linki" },
  { key: "accent", label: "Akcent", hint: "postęp, znacznik wyboru, fokus" },
  { key: "canvas", label: "Tło", hint: "płótno procesu" },
  { key: "ink", label: "Tekst", hint: "nagłówki i wartości" },
];

/** Odcienie, których człowiek nie wpisuje — pokazujemy je, żeby nie były niespodzianką. */
const LICZONE: { token: string; label: string }[] = [
  { token: "primary-wash", label: "zaznaczenie" },
  { token: "primary-line", label: "obrys" },
  { token: "border", label: "obrys pól" },
  { token: "muted", label: "podpisy" },
  { token: "danger", label: "błąd" },
];

/** Paleta na karcie listy: akcja, akcent, zaznaczenie, płótno, tekst. */
const PALETA = ["primary", "accent", "primary-wash", "canvas", "ink"];

type Slot = "welcome" | "final" | "fail";
type Pole = "title" | "text";

interface Wyliczone {
  tokens: BrandTokens;
  checks: BrandCheck[];
  notes: BrandNote[];
}

interface Szkic {
  key: string;
  nowy: boolean;
  name: string;
  domains: string[];
  colors: Record<string, string>;
  texts: BrandTexts;
  schemas: string[];
  logoLight: string | null;
  logoDark: string | null;
  // Pole logo jedzie do serwera tylko po zmianie — inaczej plik zostaje bez zmian.
  logoLightZmienione: boolean;
  logoDarkZmienione: boolean;
}

function zWiersza(row: BrandRow): Szkic {
  return {
    key: row.key,
    nowy: false,
    name: row.name,
    domains: [...row.domains],
    colors: { ...row.colors },
    texts: row.texts ?? {},
    schemas: [...row.schemas],
    logoLight: row.logo?.light ?? null,
    logoDark: row.logo?.dark ?? null,
    logoLightZmienione: false,
    logoDarkZmienione: false,
  };
}

function nowySzkic(wzor: BrandRow | undefined): Szkic {
  return {
    key: "",
    nowy: true,
    name: "",
    domains: [],
    // Punkt wyjścia: kolory for apps — przechodzą kontrolę, więc da się od razu zapisać.
    colors: { ...(wzor?.colors ?? {}) },
    texts: {},
    schemas: [],
    logoLight: null,
    logoDark: null,
    logoLightZmienione: false,
    logoDarkZmienione: false,
  };
}

/** „City Drive" → „city-drive": klucz trafia do adresów, więc bez znaków diakrytycznych. */
function slug(nazwa: string): string {
  return nazwa
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ł/g, "l")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function tekst(texts: BrandTexts, slot: Slot, pole: Pole, jezyk: Locale): string {
  return texts[slot]?.[pole]?.[jezyk] ?? "";
}

function zTekstem(texts: BrandTexts, slot: Slot, pole: Pole, jezyk: Locale, wartosc: string): BrandTexts {
  return {
    ...texts,
    [slot]: { ...texts[slot], [pole]: { ...texts[slot]?.[pole], [jezyk]: wartosc } },
  };
}

function wylicz(row: BrandRow): Wyliczone {
  return { tokens: row.tokens, checks: row.checks, notes: row.notes };
}

export function Marki() {
  const [rows, setRows] = useState<BrandRow[]>([]);
  const [procesy, setProcesy] = useState<{ schema_id: string; title: string | null }[]>([]);
  const [szkic, setSzkic] = useState<Szkic | null>(null);
  const [poczatek, setPoczatek] = useState("");
  const [wyliczone, setWyliczone] = useState<Wyliczone | null>(null);
  const [jezyk, setJezyk] = useState<Locale>("pl");
  const [kluczRecznie, setKluczRecznie] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const otworz = useCallback((row: BrandRow) => {
    const nowy = zWiersza(row);
    setSzkic(nowy);
    setPoczatek(JSON.stringify(nowy));
    setWyliczone(wylicz(row));
    setKluczRecznie(false);
    setError(null);
    setProblems([]);
  }, []);

  const load = useCallback(
    async (zaznacz?: string) => {
      try {
        const dane = await admin.brands();
        // Classic na końcu listy: to punkt wyjścia, a nie marka, którą się prowadzi.
        dane.items.sort((a, b) => Number(a.key === CLASSIC) - Number(b.key === CLASSIC));
        setRows(dane.items);
        setProcesy(dane.schemas);
        // Na start pierwsza marka, nie classic — classic się nie edytuje.
        const cel =
          dane.items.find((i) => i.key === zaznacz) ??
          dane.items.find((i) => i.key !== CLASSIC) ??
          dane.items[0];
        if (cel) otworz(cel);
      } catch (caught) {
        setError((caught as AdminError).message);
      }
    },
    [otworz],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const wiersz = rows.find((r) => r.key === szkic?.key && !szkic?.nowy);
  const edytowalny = szkic ? szkic.nowy || Boolean(wiersz?.editable) : false;
  const zmiany = szkic !== null && JSON.stringify(szkic) !== poczatek;
  const kolory = szkic?.colors;

  // Odcienie, kontrast i uwagi liczy serwer. Przy niezmienionych kolorach
  // wystarcza to, co przyszło z listą; classic ma tokeny wprost z tokens.css.
  useEffect(() => {
    if (!kolory || !edytowalny) return;
    if (!KOLORY.every((k) => HEX.test(kolory[k.key] ?? ""))) return;
    const zrodlo = rows.find((r) => r.key === szkic?.key);
    if (zrodlo && JSON.stringify(zrodlo.colors) === JSON.stringify(kolory)) {
      setWyliczone(wylicz(zrodlo));
      return;
    }
    const czekaj = window.setTimeout(() => {
      admin.deriveBrand(kolory).then(setWyliczone).catch(() => undefined);
    }, 250);
    return () => window.clearTimeout(czekaj);
    // Szkic podmieniamy przy każdej zmianie — efekt patrzy tylko na kolory.
  }, [kolory, edytowalny]);

  const zmien = (zmiana: Partial<Szkic>) => {
    setSzkic((obecny) => (obecny ? { ...obecny, ...zmiana } : obecny));
    setNotice(null);
  };

  const porzucicZmiany = () =>
    !zmiany || window.confirm("Masz niezapisane zmiany w szablonie. Porzucić je?");

  const wybierz = (row: BrandRow) => {
    if (row.key === szkic?.key && !szkic.nowy) return;
    if (!porzucicZmiany()) return;
    setNotice(null);
    otworz(row);
  };

  const nowy = () => {
    if (!porzucicZmiany()) return;
    const szablon = nowySzkic(rows.find((r) => r.key === CLASSIC));
    setSzkic(szablon);
    setPoczatek(JSON.stringify(szablon));
    setKluczRecznie(false);
    setError(null);
    setProblems([]);
    setNotice(null);
  };

  const zapisz = async () => {
    if (!szkic) return;
    const klucz = szkic.key.trim();
    // PUT nadpisuje — nowy szablon nie może po cichu zastąpić istniejącego.
    if (szkic.nowy && rows.some((r) => r.key === klucz)) {
      setError(`Szablon z kluczem „${klucz}” już istnieje — wybierz go z listy albo zmień klucz.`);
      return;
    }
    setBusy(true);
    setError(null);
    setProblems([]);
    try {
      const body: BrandPayload = {
        name: szkic.name.trim(),
        domains: szkic.domains,
        colors: szkic.colors,
        texts: szkic.texts,
        schemas: szkic.schemas,
      };
      if (szkic.logoLightZmienione) body.logo_light = szkic.logoLight;
      if (szkic.logoDarkZmienione) body.logo_dark = szkic.logoDark;
      const zapisany = await admin.saveBrand(klucz, body);
      setRows((obecne) =>
        obecne.some((r) => r.key === zapisany.key)
          ? obecne.map((r) => (r.key === zapisany.key ? zapisany : r))
          : [...obecne, zapisany],
      );
      otworz(zapisany);
      setNotice("Zapisano. Sprawy z tym szablonem widzą zmiany od razu — także w wysłanych już linkach.");
    } catch (caught) {
      const blad = caught as AdminError;
      setError(blad.message);
      setProblems(blad.problems);
    } finally {
      setBusy(false);
    }
  };

  const usun = async () => {
    if (!wiersz) return;
    const pytanie = wiersz.builtin
      ? `Przywrócić „${wiersz.name}” do wartości wbudowanych? Zmiany zapisane w panelu znikną.`
      : `Usunąć szablon „${wiersz.name}”?` +
        (wiersz.records
          ? ` ${wiersz.records} spraw podało go w theme — przejdą na szablon procesu albo classic.`
          : "");
    if (!window.confirm(pytanie)) return;
    setBusy(true);
    try {
      const wynik = await admin.deleteBrand(wiersz.key);
      await load(wynik.restored ? wiersz.key : undefined);
      setNotice(wynik.restored ? "Przywrócono wartości wbudowane." : "Szablon usunięty.");
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  const wgrajLogo = (wariant: "light" | "dark", plik: File | undefined) => {
    if (!plik) return;
    if (!LOGO_TYPY.includes(plik.type)) {
      setError("Logo musi być plikiem PNG, SVG, WebP albo JPG.");
      return;
    }
    if (plik.size > LOGO_MAX) {
      setError(`Logo ma ${Math.round(plik.size / 1024)} KB — limit to ${LOGO_MAX / 1024} KB.`);
      return;
    }
    const czytnik = new FileReader();
    czytnik.onload = () => {
      const dane = String(czytnik.result);
      zmien(
        wariant === "light"
          ? { logoLight: dane, logoLightZmienione: true }
          : { logoDark: dane, logoDarkZmienione: true },
      );
    };
    czytnik.readAsDataURL(plik);
  };

  // Proces przypięty gdzie indziej nie może trafić do drugiego szablonu —
  // serwer i tak odmówi, ale lepiej nie proponować czegoś, co nie przejdzie.
  const zajete = useMemo(() => {
    const mapa = new Map<string, string>();
    rows.forEach((r) => r.key !== szkic?.key && r.schemas.forEach((s) => mapa.set(s, r.name)));
    return mapa;
  }, [rows, szkic?.key]);

  const tokens = wyliczone?.tokens ?? wiersz?.tokens;
  const oceny = new Map((wyliczone?.checks ?? []).map((c) => [c.color, c]));

  return (
    <section className="adm__section">
      <PageHead
        title="Szablony marki"
        subtitle="Kolory, logo i teksty, które kandydat widzi w procesie. Panel zostaje w barwach for apps."
        actions={
          <button className="btn btn--primary" onClick={nowy} disabled={busy}>
            Nowy szablon
          </button>
        }
      />

      <div className="marki">
        <div className="marki__lista">
          {rows.map((row) => (
            <button
              key={row.key}
              type="button"
              className={`marki__item${row.key === szkic?.key && !szkic.nowy ? " marki__item--on" : ""}`}
              aria-pressed={row.key === szkic?.key && !szkic?.nowy}
              onClick={() => wybierz(row)}
            >
              <span className="marki__itemlogo">
                <ZnakSzablonu klucz={row.key} nazwa={row.name} logo={row.logo?.light ?? null} rozmiar={22} />
              </span>
              <span>
                <span className="marki__itemname">{row.name}</span>
                <span className="marki__itemmeta">
                  theme: {row.key} · {row.key === CLASSIC ? "domyślny" : row.schemas.join(", ") || "bez procesu"}
                </span>
              </span>
              <span className="marki__paleta" aria-hidden="true">
                {PALETA.map((token) => (
                  <i key={token} className="marki__probka" style={{ background: row.tokens.light[token] }} />
                ))}
              </span>
            </button>
          ))}
          {szkic?.nowy && (
            <div className="marki__item marki__item--on marki__item--nowy">
              <span className="marki__itemname">{szkic.name || "Nowy szablon"}</span>
              <span className="marki__itemmeta">theme: {szkic.key || "…"} · niezapisany</span>
            </div>
          )}
          <p className="marki__stopka">
            Szablon to tylko skórka: układ, pola i kolejność pytań zostają te same we wszystkich markach.
          </p>
        </div>

        {szkic && (
          <div className="marki__edytor">
            <header className="marki__naglowek">
              <h2 className="marki__nazwa">{szkic.name || "Nowy szablon"}</h2>
              <span className="marki__plakietka">theme: {szkic.key || "…"}</span>
              <span className="adm__spacer" />
              {edytowalny && (
                <button className="btn btn--primary" onClick={() => void zapisz()} disabled={busy || !zmiany}>
                  Zapisz szablon
                </button>
              )}
            </header>

            {!edytowalny && (
              <p className="marki__info">
                Classic to wygląd for apps prosto z tokens.css i teksty z interfejsu — tak wygląda
                każda sprawa bez <code>theme</code> i bez szablonu procesu. Nie edytuje się; żeby
                zmienić markę, załóż nowy szablon.
              </p>
            )}

            {error && (
              <div className="adm__alert adm__alert--error" role="alert">
                {error}
                {problems.length > 0 && (
                  <ul>
                    {problems.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {notice && (
              <div className="adm__alert marki__ok" role="status">
                {notice}
              </div>
            )}

            <fieldset className="marki__sekcja" disabled={!edytowalny}>
              <Naglowek
                tytul="Identyfikacja"
                opis="Klucz trafia do API jako parametr theme. Domeny to adresy proxy, pod którymi kandydat widzi proces."
              />
              <div className="adm__grid adm__grid--2">
                <label className="adm__field">
                  <span className="adm__fieldlabel">Nazwa marki</span>
                  <input
                    className="input"
                    value={szkic.name}
                    maxLength={128}
                    onChange={(e) =>
                      zmien({
                        name: e.target.value,
                        // Klucz podąża za nazwą, dopóki człowiek nie wpisze własnego.
                        ...(szkic.nowy && !kluczRecznie ? { key: slug(e.target.value) } : {}),
                      })
                    }
                  />
                </label>
                <label className="adm__field">
                  <span className="adm__fieldlabel">Klucz (theme)</span>
                  <input
                    className="input marki__mono"
                    value={szkic.key}
                    maxLength={64}
                    // Sprawy trzymają klucz jako odnośnik — po zapisie się nie zmienia.
                    disabled={!szkic.nowy}
                    onChange={(e) => {
                      setKluczRecznie(true);
                      zmien({ key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") });
                    }}
                  />
                  <span className="marki__podpowiedz">Parametr theme w POST /api/onboarding</span>
                </label>
              </div>
              <div className="adm__field">
                <span className="adm__fieldlabel">Domeny (proxy)</span>
                <Domeny value={szkic.domains} onChange={(domains) => zmien({ domains })} />
                <span className="marki__podpowiedz">
                  Magic link w SMS-ie zbuduje się od pierwszej domeny. Pusto — adres tej instalacji.
                  Domenę dopisz, gdy CNAME i certyfikat już działają — przy zapisie sprawdzamy, czy
                  odpowiada pod nią ta instalacja.
                </span>
              </div>
            </fieldset>

            <fieldset className="marki__sekcja" disabled={!edytowalny}>
              <Naglowek
                tytul="Logo"
                opis="PNG lub SVG. Wersja na ciemne tło jest potrzebna, gdy kandydat włączy ciemny motyw."
              />
              <div className="marki__loga">
                {(["light", "dark"] as const).map((wariant) => {
                  const ciemne = wariant === "dark";
                  const src = ciemne ? szkic.logoDark : szkic.logoLight;
                  const podpis = ciemne ? "Na ciemne tło" : "Na jasne tło";
                  return (
                    <div
                      key={wariant}
                      className={`marki__logo${ciemne ? " marki__logo--ciemne" : ""}`}
                      style={ciemne ? { background: szkic.colors.ink } : undefined}
                    >
                      {szkic.key === CLASSIC ? (
                        <span className="marki__logobrak">
                          <Logo size={26} wariant={ciemne ? "reverse" : "primary"} />
                        </span>
                      ) : src ? (
                        <img src={src} alt={`${szkic.name} — ${podpis.toLowerCase()}`} />
                      ) : (
                        <span className="marki__logobrak">
                          {ciemne ? "brak — w ciemnym motywie zostanie jasne" : "brak logo"}
                        </span>
                      )}
                      {edytowalny && (
                        <span className="marki__logoakcje">
                          <label className="marki__zmien">
                            <Icons.upload size={14} />
                            {podpis} · {src ? "zmień plik" : "wgraj plik"}
                            <input
                              type="file"
                              accept={LOGO_TYPY.join(",")}
                              onChange={(e) => {
                                wgrajLogo(wariant, e.target.files?.[0]);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          {/* Usunąć da się tylko własny plik — wbudowane logo i tak by wróciło. */}
                          {src && /^(data:|\/api\/brand\/)/.test(src) && (
                            <button
                              type="button"
                              className="marki__usunlogo"
                              onClick={() =>
                                zmien(
                                  ciemne
                                    ? { logoDark: null, logoDarkZmienione: true }
                                    : { logoLight: null, logoLightZmienione: true },
                                )
                              }
                            >
                              usuń
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className="marki__sekcja" disabled={!edytowalny}>
              <Naglowek
                tytul="Kolory"
                opis="Wpisujesz kolory marki. Tła zaznaczenia, obrysy, fokus, podpisy, błąd i ciemny motyw liczymy sami — i sprawdzamy kontrast, zanim szablon się zapisze."
              />
              <div className="marki__kolory">
                {KOLORY.map(({ key, label, hint }) => {
                  const wartosc = szkic.colors[key] ?? "";
                  const poprawny = HEX.test(wartosc);
                  const ocena = oceny.get(key);
                  return (
                    <div key={key} className="marki__kolor">
                      <span className="marki__swatch" style={{ background: poprawny ? wartosc : "transparent" }}>
                        <input
                          type="color"
                          aria-label={`${label} — paleta`}
                          value={poprawny ? wartosc.toLowerCase() : "#000000"}
                          onChange={(e) =>
                            zmien({ colors: { ...szkic.colors, [key]: e.target.value.toUpperCase() } })
                          }
                        />
                      </span>
                      <span className="marki__kolorlabel">
                        <b>{label}</b>
                        <span>{hint}</span>
                      </span>
                      <input
                        className={`input marki__hex${poprawny ? "" : " marki__hex--zle"}`}
                        aria-label={label}
                        value={wartosc}
                        maxLength={7}
                        spellCheck={false}
                        onChange={(e) => {
                          const surowa = e.target.value.trim().toUpperCase();
                          zmien({
                            colors: { ...szkic.colors, [key]: surowa.startsWith("#") ? surowa : `#${surowa}` },
                          });
                        }}
                      />
                      {ocena && <Ocena ocena={ocena} />}
                    </div>
                  );
                })}
              </div>

              {(wyliczone?.notes.length ?? 0) > 0 && (
                <div className={`marki__uwaga${wyliczone?.notes.some((n) => n.kind === "warn") ? " marki__uwaga--warn" : ""}`}>
                  <Ostrzezenie />
                  <div>
                    {wyliczone?.notes.map((uwaga) => (
                      <p key={uwaga.color + uwaga.text}>
                        {uwaga.text}{" "}
                        {uwaga.suggest && edytowalny && (
                          <button
                            type="button"
                            className="marki__uzyj"
                            onClick={() => zmien({ colors: { ...szkic.colors, [uwaga.color]: uwaga.suggest! } })}
                          >
                            Użyj {uwaga.suggest}
                          </button>
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {tokens && (
                <div className="marki__liczone">
                  Liczone automatycznie:
                  {LICZONE.map(({ token, label }) => (
                    <span key={token}>
                      <i style={{ background: tokens.light[token] }} />
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </fieldset>

            <fieldset className="marki__sekcja" disabled={!edytowalny}>
              <Naglowek
                tytul="Teksty"
                opis="Powitanie stoi na pierwszym ekranie (przed kodem SMS). Tytuł finału widać po podpisie zawsze, a tekst finału tylko tam, gdzie proces nie ma własnej noty końcowej — nota mówi, co dalej na danej ścieżce (np. 2–3 miesiące czekania na zezwolenie). Odmowa — gdy konsultant zamknie sprawę odmową; bez tekstu kandydat dostaje odmowę bez numeru infolinii. Każdy język osobno; brak tłumaczenia = tekst polski."
              />
              <div className="marki__jezyki" role="group" aria-label="Język tekstów">
                {JEZYKI.map((j) => (
                  <button
                    key={j}
                    type="button"
                    className={`marki__jezyk${jezyk === j ? " marki__jezyk--on" : ""}`}
                    aria-pressed={jezyk === j}
                    onClick={() => setJezyk(j)}
                  >
                    {j.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="marki__teksty">
                {(
                  [
                    ["welcome", "title", "Powitanie — tytuł"],
                    ["final", "title", "Finał — tytuł"],
                    ["welcome", "text", "Powitanie — tekst"],
                    ["final", "text", "Finał — tekst (gdy proces nie ma noty końcowej)"],
                    ["fail", "title", "Odmowa — tytuł (z infolinią marki)"],
                    ["fail", "text", "Odmowa — tekst"],
                  ] as [Slot, Pole, string][]
                ).map(([slot, pole, etykieta]) => {
                  const polski = tekst(szkic.texts, slot, pole, "pl");
                  const wspolne = {
                    className: "input",
                    value: tekst(szkic.texts, slot, pole, jezyk),
                    maxLength: pole === "title" ? 160 : 600,
                    // Brak tłumaczenia = tekst polski — podpowiedź pokazuje, co zobaczy kandydat.
                    placeholder: jezyk === "pl" ? domyslny(slot, pole, "pl") : polski || domyslny(slot, pole, jezyk),
                    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      zmien({ texts: zTekstem(szkic.texts, slot, pole, jezyk, e.target.value) }),
                  };
                  return (
                    <label key={slot + pole} className="adm__field">
                      <span className="adm__fieldlabel">{etykieta}</span>
                      {pole === "title" ? <input {...wspolne} /> : <textarea rows={4} {...wspolne} />}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className="marki__sekcja" disabled={!edytowalny}>
              <Naglowek
                tytul="Gdzie działa"
                opis="Szablon przypięty do procesu włącza się sam. Parametr theme w zaproszeniu ma pierwszeństwo — tak jeden proces obsłuży dwie marki."
              />
              <div className="marki__gdzie">
                <span>Domyślny dla procesów:</span>
                {szkic.key === CLASSIC ? (
                  <span className="marki__plakietka">każdy bez szablonu</span>
                ) : (
                  szkic.schemas.map((id) => (
                    <span key={id} className="marki__proces">
                      {id}
                      <button
                        type="button"
                        aria-label={`Odepnij ${id}`}
                        onClick={() => zmien({ schemas: szkic.schemas.filter((s) => s !== id) })}
                      >
                        ×
                      </button>
                    </span>
                  ))
                )}
                {edytowalny && (
                  <select
                    className="select select--tight"
                    aria-label="Przypnij proces"
                    value=""
                    onChange={(e) => e.target.value && zmien({ schemas: [...szkic.schemas, e.target.value] })}
                  >
                    <option value="">+ przypnij proces</option>
                    {procesy
                      .filter((p) => !szkic.schemas.includes(p.schema_id))
                      .map((p) => (
                        <option key={p.schema_id} value={p.schema_id} disabled={zajete.has(p.schema_id)}>
                          {p.title ?? p.schema_id}
                          {zajete.has(p.schema_id) ? ` — ma „${zajete.get(p.schema_id)}”` : ""}
                        </option>
                      ))}
                  </select>
                )}
              </div>
            </fieldset>

            {wiersz && (wiersz.overridden || !wiersz.builtin) && (
              <footer className="marki__stopa">
                {wiersz.updated_by && (
                  <span className="adm__muted">
                    Ostatnio zapisał {wiersz.updated_by}
                  </span>
                )}
                <span className="adm__spacer" />
                <button className="btn btn--ghost adm__risky" onClick={() => void usun()} disabled={busy}>
                  {wiersz.builtin ? "Przywróć wartości wbudowane" : "Usuń szablon"}
                </button>
              </footer>
            )}
          </div>
        )}

        {szkic && tokens && <Podglad szkic={szkic} tokens={tokens} jezyk={jezyk} />}
      </div>
    </section>
  );
}

/** Teksty, które kandydat zobaczy bez tłumaczenia z szablonu — z interfejsu. */
function domyslny(slot: Slot, pole: Pole, jezyk: Locale): string {
  if (slot === "welcome") return t(pole === "title" ? "asideTitle" : "asideLead", jezyk);
  // Edytowalne są wszystkie marki poza classic, więc domyślna odmowa to ta bez
  // infolinii City Drive — dokładnie to, co zobaczy kandydat bez tekstu marki.
  if (slot === "fail") return pole === "title" ? t("finishedFailTitleGeneric", jezyk) : "";
  return pole === "title" ? t("finishedSuccessTitle", jezyk) : "";
}

function Naglowek({ tytul, opis }: { tytul: string; opis: string }) {
  return (
    <div>
      <h3 className="marki__sekcjatytul">{tytul}</h3>
      <p className="marki__opis">{opis}</p>
    </div>
  );
}

function Ocena({ ocena }: { ocena: BrandCheck }) {
  const rodzaj = ocena.ratio === null ? "neutral" : ocena.ok ? "ok" : "zle";
  return (
    <span className={`marki__ocena marki__ocena--${rodzaj}`}>
      {rodzaj === "ok" && <Icons.check size={12} />}
      {rodzaj === "zle" && <Icons.cross size={12} />}
      {ocena.label}
    </span>
  );
}

/** Logo z szablonu albo — dla classic i szablonu bez pliku — znak for apps. */
function ZnakSzablonu({
  klucz,
  nazwa,
  logo,
  rozmiar,
}: {
  klucz: string;
  nazwa: string;
  logo: string | null;
  rozmiar: number;
}) {
  if (logo && klucz !== CLASSIC) return <img src={logo} alt={nazwa} />;
  return (
    <>
      <Mark size={rozmiar} />
      <span className="logo__slowo" style={{ fontSize: rozmiar * 0.77 }}>
        for apps
      </span>
    </>
  );
}

function Domeny({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const [wpis, setWpis] = useState("");

  const dodaj = () => {
    const domena = wpis.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, "");
    if (domena && !value.includes(domena)) onChange([...value, domena]);
    setWpis("");
  };

  return (
    <div className="marki__domeny">
      {value.map((domena) => (
        <span key={domena} className="marki__chip">
          {domena}
          <button type="button" aria-label={`Usuń ${domena}`} onClick={() => onChange(value.filter((d) => d !== domena))}>
            ×
          </button>
        </span>
      ))}
      <input
        value={wpis}
        aria-label="Dodaj domenę"
        placeholder={value.length ? "" : "rejestracja.twoja-marka.pl"}
        onChange={(e) => setWpis(e.target.value)}
        onBlur={dodaj}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "," || e.key === " ") {
            e.preventDefault();
            dodaj();
          }
        }}
      />
      <button type="button" className="marki__dodaj" aria-label="Dodaj domenę" onClick={dodaj}>
        +
      </button>
    </div>
  );
}

function Ostrzezenie() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 4 3 19.5h18L12 4Z" />
        <path d="M12 10v4.2M12 17h.01" />
      </g>
    </svg>
  );
}

type Ekran = "powitanie" | "pytanie" | "final" | "odmowa";

/**
 * Telefon z procesem w barwach szablonu — z niezapisanych zmian.
 *
 * Makieta, nie prawdziwy proces: ekran w skali 390×844 zmniejszony do 2/3.
 * Tokeny siedzą w zmiennych na korzeniu makiety, więc reszta panelu zostaje
 * w barwach for apps. Ciemny wariant kładzie `data-theme="dark"` na makiecie:
 * neutrale bierze z tokens.css, markę z policzonych tokenów.
 */
function Podglad({ szkic, tokens, jezyk }: { szkic: Szkic; tokens: BrandTokens; jezyk: Locale }) {
  const [ekran, setEkran] = useState<Ekran>("powitanie");
  const [ciemny, setCiemny] = useState(false);
  // Odmowa to też koniec procesu — pasek i etapy jak na finale.
  const koniec = ekran === "final" || ekran === "odmowa";

  const zmienne = Object.fromEntries(
    Object.entries(ciemny ? tokens.dark : tokens.light).map(([nazwa, wartosc]) => [`--${nazwa}`, wartosc]),
  ) as React.CSSProperties;

  const napis = (slot: Slot, pole: Pole) =>
    tekst(szkic.texts, slot, pole, jezyk) || tekst(szkic.texts, slot, pole, "pl") || domyslny(slot, pole, jezyk);
  const finalTekst = tekst(szkic.texts, "final", "text", jezyk) || tekst(szkic.texts, "final", "text", "pl");
  const logo = ciemny ? (szkic.logoDark ?? szkic.logoLight) : szkic.logoLight;

  return (
    <aside className="marki__podglad" aria-label="Podgląd na żywo">
      <div className="marki__podgladnaglowek">
        <b>Podgląd na żywo</b>
        <span className="marki__plakietka">telefon</span>
      </div>
      <div className="marki__jezyki" role="group" aria-label="Ekran podglądu">
        {(
          [
            ["powitanie", "Powitanie"],
            ["pytanie", "Pytanie"],
            ["final", "Finał"],
            ["odmowa", "Odmowa"],
          ] as [Ekran, string][]
        ).map(([id, etykieta]) => (
          <button
            key={id}
            type="button"
            className={`marki__jezyk${ekran === id ? " marki__jezyk--on" : ""}`}
            aria-pressed={ekran === id}
            onClick={() => setEkran(id)}
          >
            {etykieta}
          </button>
        ))}
      </div>

      <div className="marki__telefon">
        <div className="marki__ekran">
          <div className="podglad" data-theme={ciemny ? "dark" : undefined} style={zmienne}>
            <div className="pg-pasek">
              <div className="pg-pasek__wiersz">
                {logo && szkic.key !== CLASSIC ? (
                  <img className="pg-logo" src={logo} alt="" />
                ) : (
                  <Logo size={18} wariant={ciemny ? "reverse" : "primary"} />
                )}
                <span className="pg-odstep" />
                {ekran !== "powitanie" && <span className="pg-pct">{koniec ? "100%" : "24%"}</span>}
                <span className="pg-motyw">
                  <button type="button" aria-label="Podgląd jasny" aria-pressed={!ciemny} onClick={() => setCiemny(false)}>
                    ☀
                  </button>
                  <button type="button" aria-label="Podgląd ciemny" aria-pressed={ciemny} onClick={() => setCiemny(true)}>
                    ☾
                  </button>
                </span>
              </div>
              {ekran !== "powitanie" && (
                <>
                  <div className="pg-etapy">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <span
                        key={i}
                        className={koniec || i < 1 ? "pg-etapy--on" : i === 1 ? "pg-etapy--teraz" : undefined}
                      />
                    ))}
                  </div>
                  <div className="pg-krok">
                    <img src={`/art/${koniec ? "podpis" : "tozsamosc"}.webp`} alt="" />
                    <span>
                      <b>Krok {koniec ? 5 : 2} z 5</b> · {koniec ? "Podpis" : "Tożsamość"}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="pg-tresc">
              {ekran === "powitanie" && (
                <div className="pg-karta">
                  <img className="pg-art" src="/art/start-link.webp" alt="" />
                  <div>
                    <p className="pg-tytul">{napis("welcome", "title")}</p>
                    <p className="pg-tekst">{napis("welcome", "text")}</p>
                  </div>
                  <div className="pg-telefon">
                    <span className="pg-etykieta">{t("otpTitle", jezyk)}</span>
                    <span className="pg-pole">
                      <span>+48</span>
                      <span>512 340 118</span>
                    </span>
                    <span className="pg-przycisk">{t("send", jezyk)}</span>
                    <span className="pg-podpis">{t("otpSubtitle", jezyk)}</span>
                  </div>
                </div>
              )}

              {ekran === "pytanie" && (
                <>
                  <div className="pg-karta">
                    <div>
                      <p className="pg-tytul">Jak chcesz z nami współpracować?</p>
                      <p className="pg-tekst">Od tego zależy, jaką umowę przygotujemy do podpisu.</p>
                    </div>
                    <div className="pg-wybory">
                      <span className="pg-wybor pg-wybor--on">
                        <span className="pg-wybor__ikona">
                          <Icons.user size={22} />
                        </span>
                        Osoba fizyczna
                        <span className="pg-wybor__znak">
                          <Icons.check size={14} />
                        </span>
                      </span>
                      <span className="pg-wybor">
                        <span className="pg-wybor__ikona">
                          <Icons.briefcase size={22} />
                        </span>
                        Firma (B2B)
                        <span className="pg-wybor__znak" />
                      </span>
                    </div>
                  </div>
                  <div className="pg-nawigacja">
                    <span className="pg-wstecz">‹ {t("back", jezyk)}</span>
                    <span className="pg-pomoc">{t("helpButton", jezyk)}</span>
                  </div>
                </>
              )}

              {ekran === "odmowa" && (
                <div className="pg-karta pg-final" role="alert">
                  <p className="pg-tytul">{napis("fail", "title")}</p>
                  {(tekst(szkic.texts, "fail", "text", jezyk) || tekst(szkic.texts, "fail", "text", "pl")) && (
                    <p className="pg-tekst">
                      {tekst(szkic.texts, "fail", "text", jezyk) || tekst(szkic.texts, "fail", "text", "pl")}
                    </p>
                  )}
                </div>
              )}

              {ekran === "final" && (
                <div className="pg-karta pg-final">
                  <img className="pg-art pg-art--duza" src="/art/gotowe.webp" alt="" />
                  <p className="pg-tytul">{napis("final", "title")}</p>
                  {finalTekst && <p className="pg-tekst">{finalTekst}</p>}
                  <div className="pg-dok">
                    {t("signedDocuments", jezyk)}
                    <span>
                      <Icons.document size={18} />
                      Umowa zlecenia.pdf
                    </span>
                  </div>
                  <div className="pg-dalej">
                    <Icons.spark size={20} />
                    {t(finalTekst ? "outcomeDocsNext" : "outcomeDoneNext", jezyk)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <p className="marki__stopka">
        Podgląd czyta niezapisane zmiany — tak samo wygląda link wysłany kandydatowi.
      </p>
    </aside>
  );
}
